import type { PlanModulo, PlanEtapa } from './tipos'

/**
 * Añade días calendario a una fecha ISO (YYYY-MM-DD).
 */
export function sumarDiasCalendario(fechaIso: string, dias: number): string {
  if (!fechaIso) return ''
  const partes = fechaIso.split('-').map(Number)
  if (partes.length !== 3) return fechaIso
  const [y, m, d] = partes
  const fecha = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
  fecha.setUTCDate(fecha.getUTCDate() + dias)

  const anio = fecha.getUTCFullYear()
  const mes = String(fecha.getUTCMonth() + 1).padStart(2, '0')
  const dia = String(fecha.getUTCDate()).padStart(2, '0')
  return `${anio}-${mes}-${dia}`
}

/**
 * Diferencia en días calendario entre dos fechas ISO (fFin - fIni).
 */
export function diferenciaDias(fIni: string, fFin: string): number {
  if (!fIni || !fFin) return 1
  const d1 = new Date(fIni).getTime()
  const d2 = new Date(fFin).getTime()
  const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24))
  return Math.max(1, diff + 1)
}

/**
 * Limita una fecha ISO entre min y max (YYYY-MM-DD).
 */
export function limitarFechaRango(fecha: string, min?: string | null, max?: string | null): string {
  if (!fecha) return ''
  let f = fecha
  if (min && f < min) f = min
  if (max && f > max) f = max
  return f
}

export type InfoFechasModulo = {
  fechaInicio: string
  fechaFin: string
  diasDuracion: number
  esAutomatica: boolean
}

/**
 * Distribuye las fechas de TODOS los módulos de una etapa a lo largo de todo
 * el rango horario disponible de la etapa de forma secuencial y proporcional a sus horas.
 */
export function distribuirFechasEtapa(
  modulos: PlanModulo[],
  etapa?: Pick<PlanEtapa, 'fecha_inicio' | 'fecha_fin'> | null
): Map<string, InfoFechasModulo> {
  const mapa = new Map<string, InfoFechasModulo>()
  if (!modulos || modulos.length === 0) return mapa

  const fIniEtapa = etapa?.fecha_inicio || new Date().toISOString().split('T')[0]
  const fFinEtapa = etapa?.fecha_fin || sumarDiasCalendario(fIniEtapa, 30)
  const diasTotalesEtapa = diferenciaDias(fIniEtapa, fFinEtapa)

  // Sumar horas totales de los módulos para ponderar
  const horasTotales = modulos.reduce((acc, m) => acc + (Number(m.horas) > 0 ? Number(m.horas) : 8), 0)

  let cursorFecha = fIniEtapa

  modulos.forEach((modulo, idx) => {
    const horasModulo = Number(modulo.horas) > 0 ? Number(modulo.horas) : 8
    const peso = horasTotales > 0 ? horasModulo / horasTotales : 1 / modulos.length

    // Días proporcionales asignados al módulo dentro del calendario de la etapa
    let diasAsignados = Math.max(1, Math.round(peso * diasTotalesEtapa))

    // Si es el último módulo, asegurar que llegue exactamente hasta la fecha fin de la etapa
    if (idx === modulos.length - 1 && modulos.length > 1) {
      const diasRestantes = diferenciaDias(cursorFecha, fFinEtapa)
      if (diasRestantes > 0) {
        diasAsignados = diasRestantes
      }
    }

    const fechaInicioAuto = cursorFecha
    let fechaFinAuto = sumarDiasCalendario(fechaInicioAuto, Math.max(0, diasAsignados - 1))
    if (fFinEtapa && fechaFinAuto > fFinEtapa) {
      fechaFinAuto = fFinEtapa
    }

    // Avanzar cursor para el siguiente módulo (día siguiente)
    cursorFecha = sumarDiasCalendario(fechaFinAuto, 1)
    if (fFinEtapa && cursorFecha > fFinEtapa) {
      cursorFecha = fFinEtapa
    }

    // Si el usuario guardó fechas explícitas, prevalecen
    const tieneManual = Boolean(modulo.fecha_inicio && modulo.fecha_fin)
    const fechaInicio = modulo.fecha_inicio || fechaInicioAuto
    const fechaFin = modulo.fecha_fin || fechaFinAuto

    const fIniSegura = limitarFechaRango(fechaInicio, fIniEtapa, fFinEtapa)
    const fFinSegura = limitarFechaRango(fechaFin, fIniSegura, fFinEtapa)
    const diasDuracion = diferenciaDias(fIniSegura, fFinSegura)

    mapa.set(modulo.id, {
      fechaInicio: fIniSegura,
      fechaFin: fFinSegura,
      diasDuracion,
      esAutomatica: !tieneManual,
    })
  })

  return mapa
}

/**
 * Calcula fechas para un único módulo usando el mismo motor de distribución.
 */
export function calcularFechasModulo(
  modulo: PlanModulo,
  etapa?: Pick<PlanEtapa, 'fecha_inicio' | 'fecha_fin'> | null
): InfoFechasModulo {
  const mapa = distribuirFechasEtapa([modulo], etapa)
  return (
    mapa.get(modulo.id) || {
      fechaInicio: modulo.fecha_inicio || etapa?.fecha_inicio || '',
      fechaFin: modulo.fecha_fin || etapa?.fecha_fin || '',
      diasDuracion: 1,
      esAutomatica: !modulo.fecha_inicio,
    }
  )
}

const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

/**
 * Formatea una fecha YYYY-MM-DD a 'DD Mes' (ej: '05 Oct').
 */
export function formatearFechaCorta(fechaIso?: string | null): string {
  if (!fechaIso) return 'Por definir'
  const partes = fechaIso.split('-')
  if (partes.length !== 3) return fechaIso
  const dia = parseInt(partes[2], 10)
  const mesIndex = parseInt(partes[1], 10) - 1
  return `${dia} ${MESES_CORTOS[mesIndex] || partes[1]}`
}
